import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar as CalendarPrimitive } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from './ui/utils';

export type PickerSession = { id: string; starts_at?: string | null };

export function formatPickerDate(iso: string | null | undefined): {
  weekday: string;
  date: string;
  time: string;
  isToday: boolean;
} {
  if (!iso) return { weekday: '', date: '', time: '', isToday: false };
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    return {
      weekday: d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase(),
      date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      time: d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, ' '),
      isToday: sameDay,
    };
  } catch {
    return { weekday: '', date: '', time: '', isToday: false };
  }
}

/**
 * Compact 3-card meeting picker. Sessions are passed in chronological-descending order
 * (newest first). The center card is the currently-focused meeting; left/right are the
 * chronologically adjacent occurrences. Arrows step by ±1. The calendar icon opens a
 * month-view popover highlighting days that have meetings.
 */
export function MeetingOccurrencePicker({
  sessions,
  focusedIndex,
  onSelect,
}: {
  sessions: PickerSession[];
  focusedIndex: number;
  onSelect: (id: string) => void;
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(undefined);

  const focused = focusedIndex >= 0 ? sessions[focusedIndex] : null;

  useEffect(() => {
    if (!focused?.starts_at) return;
    const d = new Date(focused.starts_at);
    if (!Number.isNaN(d.getTime())) setCalendarMonth(d);
  }, [focused?.starts_at]);

  if (!focused) return null;

  const idx = Math.max(0, focusedIndex);
  const earlier = sessions[idx + 1] || null;
  const later = sessions[idx - 1] || null;
  const goEarlier = () => earlier && onSelect(earlier.id);
  const goLater = () => later && onSelect(later.id);

  const localDateKey = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };
  const sessionsByDay = (() => {
    const map = new Map<string, PickerSession>();
    for (const s of sessions) {
      const key = localDateKey(s.starts_at);
      if (!key) continue;
      if (!map.has(key)) map.set(key, s);
    }
    return map;
  })();
  const meetingDayDates: Date[] = [...sessionsByDay.keys()].map((k) => {
    const [y, m, d] = k.split('-').map((n) => Number(n));
    return new Date(y, m - 1, d);
  });

  const onCalendarSelect = (day: Date | undefined) => {
    if (!day) return;
    const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    const target = sessionsByDay.get(key);
    if (target?.id) {
      onSelect(target.id);
      setCalendarOpen(false);
    }
  };

  const renderCard = (m: PickerSession | null, role: 'left' | 'center' | 'right'): JSX.Element | null => {
    if (!m) {
      return (
        <div
          aria-hidden
          className="hidden h-[58px] w-[124px] shrink-0 rounded-md border border-dashed border-outline-variant/20 sm:block"
        />
      );
    }
    const parts = formatPickerDate(m.starts_at);
    const isCenter = role === 'center';
    return (
      <button
        key={m.id}
        type="button"
        onClick={() => onSelect(m.id)}
        className={cn(
          'flex h-[58px] w-[124px] shrink-0 flex-col items-center justify-center rounded-md border px-2 py-1 text-center transition-colors',
          isCenter
            ? 'border-2 border-primary bg-primary/10 text-foreground'
            : 'border-outline-variant/30 bg-card hover:bg-muted/40',
        )}
        aria-current={isCenter ? 'true' : undefined}
        aria-label={`${parts.weekday} ${parts.date} ${parts.time}`}
      >
        <span className={cn('text-[9px] font-bold uppercase tracking-[0.12em]', isCenter ? 'text-primary' : 'text-muted-foreground')}>
          {parts.isToday ? `${parts.weekday} · TODAY` : parts.weekday}
        </span>
        <span className="font-display text-sm font-semibold leading-tight">{parts.date}</span>
        <span className="text-[10px] leading-none text-muted-foreground">{parts.time}</span>
      </button>
    );
  };

  return (
    <div className="flex shrink-0 items-center justify-center gap-2 border-b border-outline-variant/15 py-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={goEarlier}
        disabled={!earlier}
        aria-label="Previous meeting"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="flex items-center gap-1.5 overflow-x-auto">
        {renderCard(earlier, 'left')}
        {renderCard(focused, 'center')}
        {renderCard(later, 'right')}
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={goLater}
        disabled={!later}
        aria-label="Next meeting"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Jump to a specific date"
          >
            <CalendarDays className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <CalendarPrimitive
            mode="single"
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            selected={focused?.starts_at ? new Date(focused.starts_at) : undefined}
            onSelect={onCalendarSelect}
            modifiers={{ hasMeeting: meetingDayDates }}
            modifiersClassNames={{
              hasMeeting:
                'relative font-semibold text-primary after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-primary',
            }}
            disabled={(day) => {
              const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
              return !sessionsByDay.has(key);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
